import * as React from 'react'

import * as entities from '@owl-nest/entities'
import * as entity from '@boutique/entities'

import type { FormattedOption } from './utils'

export type SelectedVariant =
  | {
      available: boolean
      compareAtPrice: entities.base.Price | null
      id: string
      numericId: number
      image?: entities.base.Image
      price: entities.base.Price
      stock: number | null
    }
  | null
  | undefined

export type SelectedOptions = {
  [key: string]: string | null
}

export type UseProductState = {
  activeImageIndex: number
  selectedOptions: SelectedOptions
  selectedVariant: SelectedVariant
}

enum ActionType {
  RESET = 'RESET',
  SELECT_OPTION = 'SELECT_OPTION',
}

type ResetAction = {
  type: ActionType.RESET
  initialSelectionState: UseProductState
}
type SelectOptionAction = {
  type: ActionType.SELECT_OPTION
  product: entity.product.Product
  optionName: string
  optionValue: string
}
type UseProductAction = ResetAction | SelectOptionAction

export function useVariantSelection(
  product: entity.product.Product | null | undefined,
  initialSelectionState: UseProductState,
): {
  selectedOptions: SelectedOptions
  selectedVariant: SelectedVariant
  onOptionSelect: (optionName: string, optionValue: string) => void
} {
  const [state, dispatch] = React.useReducer(reducer, initialSelectionState)

  React.useEffect(() => {
    dispatch({ type: ActionType.RESET, initialSelectionState })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  return {
    selectedOptions: state.selectedOptions,
    selectedVariant: state.selectedVariant,
    onOptionSelect,
  }

  function onOptionSelect(optionName: string, optionValue: string) {
    if (product) {
      dispatch({ type: ActionType.SELECT_OPTION, optionName, optionValue, product })
    }
  }
}
export function useImageSelection(
  product: entity.product.Product | null | undefined,
  selectedVariant: SelectedVariant,
  initialSelectionState: UseProductState,
): {
  activeImageIndex: number
  onImageSelect: (index: number) => void
} {
  const [activeImageIndex, setActiveImageIndex] = React.useState(initialSelectionState.activeImageIndex)

  React.useEffect(() => {
    setActiveImageIndex(initialSelectionState.activeImageIndex)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id])

  React.useEffect(() => {
    if (product) {
      setActiveImageIndex(getActiveImageIndex(product, selectedVariant))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVariant])

  return {
    activeImageIndex,
    onImageSelect,
  }

  function onImageSelect(index: number) {
    setActiveImageIndex(index)
  }
}

function reducer(state: UseProductState, action: UseProductAction): UseProductState {
  switch (action.type) {
    case ActionType.RESET:
      return { ...action.initialSelectionState }

    case ActionType.SELECT_OPTION: {
      const selectedOptions = {
        ...state.selectedOptions,
        [action.optionName]: action.optionValue,
      }
      const selectedVariant = getSelectedVariant(action.product, selectedOptions)

      return {
        ...state,
        selectedVariant,
        selectedOptions,
      }
    }
    default:
      return state
  }
}

function getActiveImageIndex(product: entity.product.Product, selectedVariant: SelectedVariant): number {
  if (!selectedVariant) {
    return 0
  }
  const imageIndex = product.images.findIndex((image) => image.origin === selectedVariant?.image?.origin)
  if (imageIndex >= 0) {
    return imageIndex
  }
  return 0
}

function getSelectedVariant(
  product: entity.product.Product,
  selectedOptions: SelectedOptions,
): SelectedVariant | null | undefined {
  if (selectedOptions === null || Object.keys(selectedOptions).length !== product.options.length) {
    return null
  }

  const currentSelection = Object.entries(selectedOptions).map(([name, value]) => ({
    name,
    value,
  }))

  if (currentSelection.some((option) => option.value === null)) {
    return null
  }

  const matchingVariantIndex = product.variants.findIndex((variant) => {
    return variant.selectedOptions.every(
      (option) => selectedOptions[option.name] && selectedOptions[option.name] === option.value,
    )
  })

  if (matchingVariantIndex > -1) {
    const variant = product.variants[matchingVariantIndex]

    return {
      available: variant.availableForSale,
      compareAtPrice: variant.compareAtPrice,
      id: variant.id,
      image: variant.image,
      numericId: variant.numericId,
      price: variant.price,
      stock: variant.quantityAvailable,
    }
  }

  return undefined
}

export function firstAvailableVariant(
  options: FormattedOption[],
  product: entity.product.Product,
  optionIndex = 0,
  selectedOptions: SelectedOptions = {},
): UseProductState {
  if (optionIndex >= options.length) {
    const selectedVariant = getSelectedVariant(product, selectedOptions)
    return {
      activeImageIndex: selectedVariant ? getActiveImageIndex(product, selectedVariant) : 0,
      selectedVariant,
      selectedOptions,
    }
  } else {
    for (const option of options[optionIndex].values) {
      if (option.available) {
        selectedOptions[options[optionIndex].name] = option.name
        const foundVariant = firstAvailableVariant(options, product, optionIndex + 1, selectedOptions)
        if (foundVariant.selectedVariant?.available) return foundVariant
      }
    }
  }

  return { activeImageIndex: 0, selectedVariant: null, selectedOptions: {} }
}

/* Server-side selection state initialization */
export function getInitialSelectionState(
  product: entity.product.Product,
  formattedOptions: {
    sizeOptions: FormattedOption[]
    nonSizeOptions: FormattedOption[]
  },
): UseProductState {
  const availableVariant = firstAvailableVariant(
    [...formattedOptions.sizeOptions, ...formattedOptions.nonSizeOptions],
    product,
  )

  if (!availableVariant.selectedVariant) {
    return {
      activeImageIndex: 0,
      selectedOptions: {},
      selectedVariant: null,
    }
  }

  return availableVariant
}
